"use client";

import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { ArrowRight, Calendar, Car, Clock, MapPin, BarChart, Bell, Shield, Truck, Users, Laptop, Loader2 } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Skeleton } from "@/components/ui/skeleton";
import { createClient } from "@/lib/client";
import { CheckIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import Gleap from 'gleap';

dayjs.locale("fr");

const fetchStats = async () => {
  const supabase = await createClient()

  const { data, error } = await supabase.rpc('get_dashboard_stats')
  if (error) throw error

  console.log(data[0])

  return {
    missions: data[0].total_missions || 0,
    drivers: data[0].total_drivers || 0,
    vehicles: data[0].total_vehicles || 0,
  };

};

const Home = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: stats = { missions: 0, drivers: 0, vehicles: 0 }, isLoading } = useQuery({
    queryKey: ["landing-stats"],
    queryFn: fetchStats,
  });

  const sendFormMutation = useMutation({
    mutationFn: async (email: string) => {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from("leads")
        .insert([
          { email }
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Merci de votre intérêt ! Nous vous contacterons rapidement.");
      setEmail("");
    },
    onError: (error) => {
      console.error("Error sending form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Veuillez saisir votre email");
      return;
    }
    await sendFormMutation.mutate(email);
  };

  const router = useRouter();

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    // only if not localhost
    if (process.env.NODE_ENV !== "development") {
      Gleap.initialize("O5nCHj67ErLQsIKec0ILktIhRMiJIPL5");

    }
  }, [])

  return (
    <>
      <Navbar scrollToSection={scrollToSection} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16">
        {/* Hero Section */}
        <section id="hero" className="px-4 py-20 text-center sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Gérez vos équipes avec
              <span className="bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {" "}
                Heroad
              </span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              La solution complète pour la gestion de votre entreprise de transport VTC.
              Simplifiez vos opérations, optimisez vos ressources et augmentez votre productivité.
            </p>
            <p className="mt-4 text-sm font-semibold text-foreground animate-pulse">
              🚀 En bêta test ! Les 10 premiers clients bénéficieront d'une offre à vie exclusive
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, 'contact')}
                className="rounded-md bg-foreground px-3.5 py-2.5 text-sm font-semibold text-background shadow-sm hover:bg-foreground/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"
              >
                Devenir bêta-testeur
              </a>
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, 'contact')}
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Nous contacter <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section id="stats" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Faites partie dès le début de l'aventure Heroad
            </h2>
            <p className="text-lg text-gray-600">
              Rejoignez une communauté grandissante de professionnels du transport qui nous font confiance
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-6">
                <Calendar className="h-8 w-8 text-gray-600" />
                <h3 className="mt-4 text-3xl font-bold">
                  {isLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    stats?.missions
                  )}
                </h3>
                <p className="text-sm text-gray-600">Missions réalisées</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-6">
                <Users className="h-8 w-8 text-gray-600" />
                <h3 className="mt-4 text-3xl font-bold">
                  {isLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    stats?.drivers
                  )}
                </h3>
                <p className="text-sm text-gray-600">Chauffeurs actifs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-6">
                <Car className="h-8 w-8 text-gray-600" />
                <h3 className="mt-4 text-3xl font-bold">
                  {isLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    stats?.vehicles
                  )}
                </h3>
                <p className="text-sm text-gray-600">Véhicules en service</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Fonctionnalités principales
          </h2>
          <p className="mt-6 text-center text-lg leading-8 text-gray-600">
            Des outils puissants pour optimiser votre activité VTC
          </p>
          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <MapPin className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Géolocalisation des chauffeurs en temps réel</h3>
                <p className="mt-2 text-gray-600">Suivez vos chauffeurs en temps réel et optimisez leurs itinéraires pour une meilleure efficacité.</p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <Bell className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Notification des nouvelles missions directement sur le smartphone</h3>
                <p className="mt-2 text-gray-600">Vos chauffeurs reçoivent instantanément leurs missions et peuvent les accepter en un clic.</p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <BarChart className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Analyses et rapports</h3>
                <p className="mt-2 text-gray-600">Suivez vos performances et prenez des décisions éclairées grâce à des rapports détaillés.</p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <Calendar className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Gestion de planning connectée</h3>
                <p className="mt-2 text-gray-600">Organisez facilement les emplois du temps de vos chauffeurs et gérez les disponibilités en temps réel.</p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Gestion optimale des chauffeurs</h3>
                <p className="mt-2 text-gray-600">Suivez les performances, gérez les documents et optimisez l'attribution des missions à vos chauffeurs.</p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-foreground text-background mb-4">
                  <Laptop className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Multi-support</h3>
                <p className="mt-2 text-gray-600">Accédez à Heroad depuis n'importe quel appareil : ordinateur, tablette ou smartphone.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="relative bg-gray-50 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Des forfaits adaptés à vos besoins
              </p>
              <div className="mt-4">
                <span className="inline-flex items-center rounded-md bg-muted px-4 py-2 text-sm font-medium text-foreground ring-1 ring-inset ring-border">
                  🎉 Offre spéciale : -50% à vie pour les 10 premiers bêta-testeurs !
                </span>
              </div>
            </div>

            <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8">
              {/* Forfait Artisan */}
              <div className="flex flex-col bg-white rounded-3xl p-8 ring-1 ring-gray-200 xl:p-10 transition-all duration-200 hover:shadow-lg">
                <h3 className="text-lg font-semibold leading-8 text-gray-900">Indépendant</h3>
                <p className="mt-4 text-sm leading-6 text-gray-600">
                  Parfait pour les indépendants et les auto-entrepreneurs
                </p>
                <div className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-bold tracking-tight text-gray-900">30€</span>
                  <span className="text-sm font-semibold leading-6 text-gray-600">/mois</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  15€/mois pour les bêta-testeurs
                </div>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    Jusqu'à 800 réservations par an incluses
                  </li>
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    0,70€ par réservation supplémentaire
                  </li>
                </ul>
                <a
                  href="#contact"
                  className="mt-8 block rounded-md bg-white px-3 py-2 text-center text-sm font-semibold leading-6 text-gray-600 ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                >
                  Commencer
                </a>
              </div>

              {/* Forfait Confirmé */}
              <div className="flex flex-col rounded-3xl p-8 ring-2 ring-foreground xl:p-10 lg:mt-[-1rem] lg:mb-[-1rem] relative bg-white shadow-xl transition-all duration-200 hover:shadow-2xl">
                <div className="absolute -top-5 left-0 right-0 mx-auto w-32 rounded-full bg-foreground px-3 py-2 text-center text-sm font-semibold text-white">Populaire</div>
                <h3 className="text-lg font-semibold leading-8 text-gray-900">Confirmé</h3>
                <p className="mt-4 text-sm leading-6 text-gray-600">
                  Idéal pour les entreprises en pleine croissance
                </p>
                <div className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-bold tracking-tight text-gray-900">60€</span>
                  <span className="text-sm font-semibold leading-6 text-gray-600">/mois</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  30€/mois pour les bêta-testeurs
                </div>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    Jusqu'à 2000 réservations par an incluses
                  </li>
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    0,60€ par réservation supplémentaire
                  </li>
                </ul>
                <a
                  href="#contact"
                  className="mt-8 block rounded-md bg-foreground px-3 py-2 text-center text-sm font-semibold leading-6 text-white hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                >
                  Commencer
                </a>
              </div>

              {/* Forfait Expert */}
              <div className="flex flex-col bg-white  rounded-3xl p-8 ring-1 ring-gray-200 xl:p-10 transition-all duration-200 hover:shadow-lg">
                <h3 className="text-lg font-semibold leading-8 text-gray-900">Expert</h3>
                <p className="mt-4 text-sm leading-6 text-gray-600">
                  Pour les entreprises avec un volume important
                </p>
                <div className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-bold tracking-tight text-gray-900">120€</span>
                  <span className="text-sm font-semibold leading-6 text-gray-600">/mois</span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  60€/mois pour les bêta-testeurs
                </div>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    Jusqu'à 5000 réservations par an incluses
                  </li>
                  <li className="flex gap-x-3">
                    <CheckIcon className="h-6 w-5 flex-none text-gray-600" aria-hidden="true" />
                    0,50€ par réservation supplémentaire
                  </li>
                </ul>
                <a
                  href="#contact"
                  className="mt-8 block rounded-md bg-white px-3 py-2 text-center text-sm font-semibold leading-6 text-gray-600 ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                >
                  Commencer
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="relative bg-white py-24 sm:py-32  px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-md text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Prêt à révolutionner votre gestion de travail ?
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Contactez-nous dès maintenant pour découvrir comment Heroad peut transformer votre entreprise
            </p>
            <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-4">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  disabled={sendFormMutation.isPending}
                  className="block w-full rounded-xl border-0 px-4 py-4 text-foreground shadow-sm 
             ring-1 ring-inset ring-foreground placeholder:text-gray-400 
             focus:ring-0 focus:outline focus:outline-[3px] focus:outline-[#00000] 
             sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {sendFormMutation.isPending ? (
                    <Loader2 className="h-5 w-5 text-gray-600 animate-spin" />
                  ) : (
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={sendFormMutation.isPending || !email}
                className="rounded-xl bg-foreground px-4 py-4 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 transition-all duration-200 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {sendFormMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </span>
                ) : (
                  "Devenir bêta-testeur"
                )}
              </button>
            </form>
            <p className="mt-4 text-sm text-gray-500">
              En vous inscrivant, vous acceptez de recevoir des emails de notre part.
              Vous pourrez vous désinscrire à tout moment.
            </p>
          </div>

          {/* Background decoration */}
          <div className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl" aria-hidden="true">
            <div
              className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-gray-600 to-indigo-600 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
              }}
            />
          </div>
        </section>
      </div>
      <Footer scrollToSection={scrollToSection} />
    </>
  );
}

export default Home;
